import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { Form, Button, Modal, Spinner } from "react-bootstrap";
import SignaturePad from "react-signature-canvas";
import { toast } from "react-toastify";
import WebService from "../utility/WebService";
import { Border } from "react-bootstrap-icons";

interface SolutionCode {
  description: string;
  isSelected: boolean;
  stdAmt?: number;
  valueAmt?: number;
  imageUrl?: string;
  linkUrl?: string;
  isAddon?: boolean;
  solnCode?: string;
  showNotes?: boolean;
  notes?: string;
  quantity?: number;
  optionNumber?: number;
}

interface PricingOption {
  optionNumber: number;
  label: string;
  headerColor: string;
  bodyColor: string;
  isSelected: boolean;
  stdAmount: number;
  valueAmount: number;
  solutionCodes: SolutionCode[];
}

interface ServiceQuoteInclusionExclusion {
  companyCode: string;
  inclusionOrExclusion: string;
  quoteNum: Number;
  sequence: Number;
  sortId: number;
  detail: string;
}

interface QuoteData {
  options: PricingOption[];
  contactName?: string;
  smEmail?: string;
  termsAndConditionDescription?: string;
  servMasterNum: string;
  valueOrStandard?: string;
  savingsLabel?: string;
  potentialSavingsLabel?: string;
  inclusionsExclusions: ServiceQuoteInclusionExclusion[];
}

interface PropData {
  optionsdata: PricingOption[];
  companyCode: string;
  quoteNum: string;
  url: string;
  terms: any;
  techNum: string;
  smContact: string;
  onClose?: (status?: boolean) => void;
}

const SignServiceQuoteWithOptionsApprove = ({
  optionsdata,
  companyCode,
  quoteNum,
  url,
  terms,
  techNum,
  smContact,
  onClose,
}: PropData) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<QuoteData | null>(null);
  const [showTerms, setShowTerms] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [signatureBy, setSignatureBy] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [emailError, setEmailError] = useState("");
  const [showSignature, setShowSignature] = useState(false); 
  const sigPadRef = useRef<SignaturePad>(null);

  // Load quote data
  const loadData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/QuotePricingOptions/getCustomerView`, {
        params: { CompanyCode: companyCode, QuoteNum: quoteNum, techNum: "0" },
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      const resData = res.data?.data;
      if (!resData) {
        toast.error("No quote data found.");
        return;
      }
      resData.termsAndConditionDescription = terms;
      setData(resData);
      setSignatureBy(smContact || "");
      setEmailTo(resData.smEmail || "");
    } catch (error: any) {
      console.error(error);
      toast.error(error.response?.data?.message || error.message || "Failed to load quote details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const validateEmail = (email: string) => email === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailTo(value);
    if (!validateEmail(value)) setEmailError("Please enter a valid email address.");
    else setEmailError("");
  };

  const clearSignature = () => sigPadRef.current?.clear();

  const onSubmit = async () => {
    if (!accepted) {
      alert("You must accept the terms and conditions before submitting.");
      return;
    }
    if (!signatureBy || !emailTo) {
      alert("Please fill all the fields.");
      return;
    }

    try {
      setLoading(true);
       if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
        alert("Please provide a signature first!");
        return;
        }
      const signatureData = sigPadRef.current.toDataURL();
      const base64 = signatureData.replace("data:image/png;base64,", "");

      const payload = {
        companyCode,
        quoteNum,
        signature: base64,
        signedBy: signatureBy
       };

      const headers = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      };

     const res = await axios.post(`${WebService.getUrl()}api/QuotePricingOptions/acceptQuote`, payload, { headers });

      if (!res.data?.success) {
        toast.error(res.data?.errorInfo?.Message || "Failed to approve quote");
        setLoading(false);
        return;
      }

      toast.success("Quote Approved Successfully");
      await sendEmail();
      onClose?.(true);
    } catch (error) {
      console.error(error);
      toast.error("Error approving quote");
    } finally {
      setLoading(false);
    }
  };

  const sendEmail = async () => {
    if (!emailTo) return;

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      };
      const payload = {
        companyCode,
        quoteNum,
        serviceMaster: data?.servMasterNum,
        techNum: techNum,
        toEmails: emailTo ? [emailTo] : [],
        bccEmails: [],
        comments: "--QuoteApproval--",
      };
      await axios.post(`${WebService.getUrl()}api/quote/email`, payload, { headers });
      toast.success("Email sent successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to send email");
    }
  };

  // Totals
  const membershipAmount = (optionsdata ?? []).filter(x => x.isSelected).reduce((sum, item) => sum + (item.valueAmount ?? 0), 0);
  const standardAmount = (optionsdata ?? []).filter(x => x.isSelected).reduce((sum, item) => sum + (item.stdAmount ?? 0), 0);
  const savingsAmt = standardAmount - membershipAmount;
  const savingsLabel = data?.valueOrStandard === "S" ? data?.potentialSavingsLabel : data?.savingsLabel;

  const formatCurrency = (value: number) =>
    value.toLocaleString("en-US", { style: "currency", currency: "USD" });

  if (loading && !data) return <Spinner animation="border" />;

  return (
<>
    
<style>
  {`
    .sawin-button {
      border: 0;
      cursor: pointer;
      background: #F55943;
      border-radius: 5px;
      line-height: 20px;
    }

.sawin-button:hover {
  background-color: #F55943;
  color: var(--white);
}

  `}
</style>


    <div style={{ padding: 10 }}>
      {/* TOTALS */}
      <div className="row text-center fw-bold mb-3">
        <div className="col-4">{formatCurrency(membershipAmount)} <br /><small>Member</small></div>
        <div className="col-4">{formatCurrency(standardAmount)} <br /><small>Non Member</small></div>
        <div className="col-4 text-success">{formatCurrency(savingsAmt)} <br /><small>{savingsLabel}</small></div>
      </div>
<hr style={{ border: "2px solid red" }} />

      {/* PRICING OPTIONS */}
      {optionsdata?.filter(option => option.isSelected).map((option, optionIndex) => (
        <div key={option.optionNumber ?? optionIndex} className="card shadow-sm mb-4 rounded-4 overflow-hidden">
          {/* Header */}
          <div className="card-header text-center" style={{ backgroundColor: option.headerColor, fontWeight: 600, fontSize: "1.0rem", padding: "0.75rem 1rem" }}>
            <div className="d-flex justify-content-between align-items-center">
              <div>{option.label}</div>
              <div>{formatCurrency(data?.valueOrStandard === "S" ? option.stdAmount : option.valueAmount)}</div>
            </div>
          </div>

          {/* Body */}
          <div className="card-body" style={{ backgroundColor: option.bodyColor, padding: "1rem 1.25rem", fontSize: "0.85rem" }}>
            {/* Selected solutions */}
            {(option.solutionCodes ?? []).filter(sc => sc.isSelected && !sc.isAddon).map((sc, scIndex) => (
              <div key={sc.solnCode ?? scIndex} className="mb-3">
                <div className="row mb-2 align-items-start">
                  <div className="col-3">
                    {sc.linkUrl ? (
                      <a href={sc.linkUrl} target="_blank" rel="noreferrer">
                        <img src={sc.imageUrl} style={{ maxHeight: "100%", maxWidth: "100%" }} />
                      </a>
                    ) : <img src={sc.imageUrl} style={{ maxHeight: "100%", maxWidth: "100%" }} />}
                  </div>
                  <div className="col-9">
                    <div className="fw-bold" style={{ fontSize: "0.9rem" }}>{sc.description}</div>
                    {sc.showNotes && <div className="mt-2" dangerouslySetInnerHTML={{ __html: sc.notes ?? "" }} />}
                  </div>
                </div>

                <div className="row" style={{ borderBottom: "1px solid rgba(0,0,0,0.1)", paddingBottom: 5 }}>
                  <div className="col-3 fw-bold">Qty <br />{sc.quantity ?? 0}</div>
                  <div className="col-9 text-end d-flex justify-content-end">
                    <div className="text-center ms-3 fw-bold">
                      Non Member<br />{formatCurrency(sc.stdAmt ?? 0)}
                    </div>
                    <div className="text-center ms-3 fw-bold">
                      Member<br />{formatCurrency(sc.valueAmt ?? 0)}
                    </div>
                    <div className="text-center ms-3 fw-bold">
                      {data?.valueOrStandard === "S" ? data?.potentialSavingsLabel : savingsLabel}<br />
                      {formatCurrency((sc.stdAmt ?? 0) - (sc.valueAmt ?? 0))}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Recommendations */}
            <div className="row mt-3 mb-2">
              <div className="col-12 fw-semibold" style={{ fontSize: "0.9rem" }}>RECOMMENDATIONS</div>
            </div>
            {(option.solutionCodes ?? []).filter(sc => !sc.isSelected && !sc.isAddon).map((sc, recIndex) => (
              <div key={sc.solnCode ?? recIndex} className="mb-2">
                <div className="row align-items-start">
                  <div className="col-3">
                    <img src={sc.imageUrl} style={{ maxHeight: "100%", maxWidth: "100%" }} />
                  </div>
                  <div className="col-9">
                    <div className="form-check form-switch fw-bold">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`recmSelected${optionIndex}_${recIndex}`}
                        defaultChecked={sc.isSelected}
                        data-soln-code={sc.solnCode}
                        data-option-number={sc.optionNumber}
                        data-quantity={sc.quantity}
                      />
                      <label className="form-check-label" htmlFor={`recmSelected${optionIndex}_${recIndex}`}>
                        {sc.description}
                      </label>
                    </div>
                    {sc.showNotes && <div className="mt-1" dangerouslySetInnerHTML={{ __html: sc.notes ?? "" }} />}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Inclusion / Exclusion */}
      <div className="row">
        <div className="col-sm-6 col-12 pr-0 mb-3">
          <div className="card shadow-sm rounded-4 overflow-hidden">
            <div className="card-header fw-bold">Inclusion</div>
            <div className="card-body">
              <ul>
                {(data?.inclusionsExclusions ?? []).filter(item => item.inclusionOrExclusion === "I").map((item, idx) => (
                  <li key={idx}>{item.detail}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="col-sm-6 col-12 pl-0 mb-3">
          <div className="card shadow-sm rounded-4 overflow-hidden">
            <div className="card-header fw-bold">Exclusions</div>
            <div className="card-body">
              <ul>
                {(data?.inclusionsExclusions ?? []).filter(item => item.inclusionOrExclusion === "E").map((item, idx) => (
                  <li key={idx}>{item.detail}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Signature Section */}

      <div>
        <Button variant="link" onClick={() => setShowTerms(true)} style={{ padding: 0 }}>
          View Terms & Conditions
        </Button>

        <Form.Check
          type="checkbox"
          label="I have reviewed and accept the terms and conditions"
          checked={accepted}
          onChange={(e) => {
            setAccepted(e.target.checked);
            setShowSignature(e.target.checked);
          }}
          className="mb-3" />
      { showSignature && <div>
        <Form.Group className="mb-3">
          <Form.Label>Signed By</Form.Label>
          <Form.Control type="text" value={signatureBy} onChange={(e) => setSignatureBy(e.target.value)} />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Signature</Form.Label>
          <div style={{ border: "1px solid #ccc", padding: 10, borderRadius: 5, maxWidth: 450, background: "#fafafa" }}>
            <SignaturePad ref={sigPadRef} canvasProps={{ width: 420, height: 150, className: "sigCanvas", style: { backgroundColor: "white" } }} />
            <div className="text-end mt-2">
              <Button variant="secondary" size="sm" onClick={clearSignature}>Clear</Button>
            </div>
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control type="email" value={emailTo} onChange={handleEmailChange} isInvalid={!!emailError} />
          <Form.Control.Feedback type="invalid">{emailError}</Form.Control.Feedback>
        </Form.Group>

        <Button variant="primary" onClick={onSubmit} disabled={loading} className="w-100 sawin-button primary-button">
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </div>}
        <Modal show={showTerms} onHide={() => setShowTerms(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Terms & Conditions</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "60vh", overflowY: "auto" }}>
            <div dangerouslySetInnerHTML={{ __html: data?.termsAndConditionDescription || "" }} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowTerms(false)}>Close</Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
      </>
  );

};

export default SignServiceQuoteWithOptionsApprove;
